import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, QrCode, RefreshCw, Save, Trash2 } from 'lucide-react';
import type { OfficialWeChatLiveCodeConfig, OfficialWeChatQrCodeEntry } from '../types';
import {
  OFFICIAL_WECHAT_QR_ROTATION_INTERVAL_DAYS,
  fetchOfficialWeChatLiveCodeConfig,
  getActiveOfficialWeChatQrCode,
  saveOfficialWeChatLiveCodeConfig,
  sanitizeOfficialWeChatLiveCodeConfig
} from '../storage';

const OFFICIAL_WECHAT_LIVE_CODE_VERSION_KEY = 'kendama-official-wechat-live-code-version';
const OFFICIAL_WECHAT_LIVE_CODE_UPDATED_EVENT = 'kendama-official-wechat-live-code-updated';
const MAX_QR_FILE_SIZE = 1024 * 1024;

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('文件读取失败'));
    };
    reader.onerror = () => reject(reader.error || new Error('文件读取失败'));
    reader.readAsDataURL(file);
  });

const formatDateTime = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return '--';
  return new Date(value).toLocaleString();
};

const moveItem = <T,>(items: T[], fromIndex: number, toIndex: number) => {
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) {
    return items;
  }
  const nextItems = [...items];
  const [item] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, item);
  return nextItems;
};

const AdminWeChatLiveCode: React.FC = () => {
  const [config, setConfig] = useState<OfficialWeChatLiveCodeConfig>(() => sanitizeOfficialWeChatLiveCodeConfig(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      try {
        setLoading(true);
        setMessage('');
        const remoteConfig = await fetchOfficialWeChatLiveCodeConfig();
        if (!mounted) return;
        setConfig(sanitizeOfficialWeChatLiveCodeConfig(remoteConfig));
      } catch (error) {
        if (!mounted) return;
        setMessage((error as Error).message || '加载微信群活码失败');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void loadConfig();

    return () => {
      mounted = false;
    };
  }, []);

  const activeInfo = useMemo(() => getActiveOfficialWeChatQrCode(config), [config]);

  const handleUploadFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (files.length === 0) return;

    const invalidTypeFile = files.find(file => !file.type.startsWith('image/'));
    if (invalidTypeFile) {
      setMessage('请只上传图片格式的二维码。');
      return;
    }

    const oversizeFile = files.find(file => file.size > MAX_QR_FILE_SIZE);
    if (oversizeFile) {
      setMessage('单张二维码图片请控制在 1MB 以内。');
      return;
    }

    try {
      const uploadedImages = await Promise.all(files.map(file => readFileAsDataUrl(file)));
      const now = Date.now();
      const newEntries: OfficialWeChatQrCodeEntry[] = uploadedImages.map((imageUrl, index) => ({
        id: `official-wechat-qr-${now}-${index + 1}`,
        imageUrl,
        createdAt: now + index
      }));

      setConfig(previous => {
        const nextConfig = sanitizeOfficialWeChatLiveCodeConfig({
          ...previous,
          entries: [...previous.entries, ...newEntries],
          rotationStartedAt: previous.rotationStartedAt || now,
          updatedAt: previous.updatedAt || now
        });
        return nextConfig;
      });
      setMessage(`已添加 ${newEntries.length} 张二维码，保存后生效。`);
    } catch (error) {
      setMessage((error as Error).message || '二维码图片读取失败');
    }
  };

  const handleRemoveEntry = (entryId: string) => {
    setConfig(previous => sanitizeOfficialWeChatLiveCodeConfig({
      ...previous,
      entries: previous.entries.filter(entry => entry.id !== entryId)
    }));
    setMessage('已移除二维码，保存后生效。');
  };

  const handleMoveEntry = (entryId: string, direction: -1 | 1) => {
    setConfig(previous => {
      const currentIndex = previous.entries.findIndex(entry => entry.id === entryId);
      const nextIndex = currentIndex + direction;
      return sanitizeOfficialWeChatLiveCodeConfig({
        ...previous,
        entries: moveItem(previous.entries, currentIndex, nextIndex)
      });
    });
    setMessage('已调整二维码顺序，保存后生效。');
  };

  const handleRestartRotation = () => {
    const now = Date.now();
    setConfig(previous => sanitizeOfficialWeChatLiveCodeConfig({
      ...previous,
      rotationStartedAt: now
    }));
    setMessage('轮换起点已重置为现在，保存后生效。');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage('');
      const savedConfig = await saveOfficialWeChatLiveCodeConfig({
        ...config,
        rotationStartedAt: config.rotationStartedAt || Date.now(),
        updatedAt: Date.now()
      });
      setConfig(savedConfig);
      window.localStorage.setItem(OFFICIAL_WECHAT_LIVE_CODE_VERSION_KEY, String(Date.now()));
      window.dispatchEvent(new Event(OFFICIAL_WECHAT_LIVE_CODE_UPDATED_EVENT));
      setMessage('微信群活码已保存。');
    } catch (error) {
      setMessage((error as Error).message || '保存微信群活码失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-12 text-center font-orbitron text-zinc-500 animate-pulse">LOADING QR CONFIG...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-emerald-500/25 bg-black/45 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-orbitron text-sm uppercase tracking-[0.18em] text-emerald-200">
              <QrCode size={16} />
              微信群活码
            </div>
            <div className="mt-2 max-w-2xl text-sm leading-6 text-zinc-300">
              管理员可上传多张官方群二维码，系统会按列表顺序每 {OFFICIAL_WECHAT_QR_ROTATION_INTERVAL_DAYS} 天自动切换下一张。
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex cursor-pointer items-center justify-center rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-emerald-200 transition-colors hover:border-emerald-300/50 hover:bg-emerald-400/20">
              上传二维码
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                multiple
                onChange={(event) => void handleUploadFiles(event)}
                className="hidden"
              />
            </label>
            <button
              type="button"
              onClick={handleRestartRotation}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-zinc-200 transition-colors hover:border-white/20 hover:bg-white/10"
            >
              <RefreshCw size={14} />
              从现在开始轮换
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/15 px-4 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-cyan-100 transition-colors hover:border-cyan-300/50 hover:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? '保存中...' : '保存活码'}
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-zinc-950/70 p-4">
            <div className="text-[11px] font-orbitron uppercase tracking-[0.16em] text-zinc-500">当前生效</div>
            <div className="mt-2 text-sm text-white">
              {activeInfo.entry ? `第 ${activeInfo.activeIndex + 1} 张 / 共 ${activeInfo.totalEntries} 张` : '未配置二维码'}
            </div>
            <div className="mt-2 text-xs leading-5 text-zinc-400">
              开始时间：{formatDateTime(activeInfo.startedAt)}
              <br />
              切换时间：{formatDateTime(activeInfo.endsAt)}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-zinc-950/70 p-4">
            <div className="text-[11px] font-orbitron uppercase tracking-[0.16em] text-zinc-500">轮换规则</div>
            <div className="mt-2 text-sm leading-6 text-zinc-300">
              每张二维码固定生效 {OFFICIAL_WECHAT_QR_ROTATION_INTERVAL_DAYS} 天，循环完最后一张后重新回到第 1 张。
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-zinc-950/70 p-4">
            <div className="text-[11px] font-orbitron uppercase tracking-[0.16em] text-zinc-500">上传说明</div>
            <div className="mt-2 text-sm leading-6 text-zinc-300">
              支持 PNG、JPG、WEBP、SVG。
              <br />
              建议上传方形二维码图，单张不超过 1MB。
            </div>
          </div>
        </div>

        {message && (
          <div className="mt-4 rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">
            {message}
          </div>
        )}
      </div>

      {config.entries.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 bg-black/35 px-4 py-10 text-center text-zinc-500">
          还没有上传任何二维码。
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {config.entries.map((entry, index) => {
            const isActive = index === activeInfo.activeIndex;
            return (
              <div
                key={entry.id}
                className={`rounded-3xl border p-4 transition-colors ${
                  isActive
                    ? 'border-emerald-400/45 bg-emerald-500/10'
                    : 'border-white/10 bg-black/40'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-orbitron text-sm uppercase tracking-[0.16em] text-white">
                      二维码 {index + 1}
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">
                      上传时间：{formatDateTime(entry.createdAt)}
                    </div>
                  </div>
                  <div className={`rounded-full border px-2 py-1 text-[10px] font-orbitron uppercase tracking-[0.14em] ${
                    isActive
                      ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200'
                      : 'border-white/10 bg-white/5 text-zinc-400'
                  }`}>
                    {isActive ? '当前生效' : `顺位 ${index + 1}`}
                  </div>
                </div>

                <div className="mt-4 overflow-hidden rounded-3xl border border-white/10 bg-white p-3">
                  <img
                    src={entry.imageUrl}
                    alt={`微信群二维码 ${index + 1}`}
                    className="aspect-square w-full rounded-2xl object-contain"
                  />
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleMoveEntry(entry.id, -1)}
                    disabled={index === 0}
                    className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2 text-zinc-300 transition-colors hover:border-white/20 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                    title="上移"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveEntry(entry.id, 1)}
                    disabled={index === config.entries.length - 1}
                    className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2 text-zinc-300 transition-colors hover:border-white/20 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                    title="下移"
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveEntry(entry.id)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-red-200 transition-colors hover:border-red-300/40 hover:bg-red-400/15"
                  >
                    <Trash2 size={14} />
                    删除
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminWeChatLiveCode;
